import { useState, useMemo } from 'react'
import { ZoningType } from '@popety-io/types'
import { ZONING_TYPE_PRIORITY } from '../utils/zoningConstants'
import {
  ZoningDataByType,
  getAvailableZoningTypes,
  getDefaultZoningType,
  getZoneCounts,
} from '../utils/zoningHelpers'

export interface TabWithCount {
  type: ZoningType
  count: number
  disabled: boolean
}

export interface UseZonePrioritizationReturn {
  allTabs: TabWithCount[]
  availableTabs: ZoningType[]
  activeTab: ZoningType | null
  setActiveTab: (type: ZoningType) => void
}

/**
 * Hook to manage zoning type prioritization and tab selection
 */
export const useZonePrioritization = (
  data: ZoningDataByType | null
): UseZonePrioritizationReturn => {
  // Calculate available tabs based on data
  const availableTabs = useMemo(() => {
    if (!data) return []
    return getAvailableZoningTypes(data)
  }, [data])

  // Calculate all tabs with counts (including disabled ones)
  const allTabs = useMemo((): TabWithCount[] => {
    if (!data) {
      // Return all types with 0 count and disabled when no data
      return ZONING_TYPE_PRIORITY.map((type) => ({
        type,
        count: 0,
        disabled: true,
      }))
    }

    const zoneCounts = getZoneCounts(data)
    return ZONING_TYPE_PRIORITY.map((type) => ({
      type,
      count: zoneCounts[type],
      disabled: zoneCounts[type] === 0,
    }))
  }, [data])

  // Initialize active tab with highest priority available type
  const defaultTab = useMemo(() => {
    if (!data) return null
    return getDefaultZoningType(data)
  }, [data])

  const [activeTab, setActiveTab] = useState<ZoningType | null>(defaultTab)

  // Update active tab when default tab changes (on data load)
  useMemo(() => {
    if (defaultTab && !activeTab) {
      setActiveTab(defaultTab)
    }
  }, [defaultTab, activeTab])

  return {
    allTabs,
    availableTabs,
    activeTab,
    setActiveTab,
  }
}
